import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import AdminTab from "../components/AdminTab.jsx";
import BulkActionChoiceModal from "../components/BulkActionChoiceModal.jsx";
import ChangeLogsTab from "../components/ChangeLogsTab.jsx";
import ContentCard from "../components/ContentCard";
import CsvWorkflow from "../components/CsvWorkflow";
import DistributionGroupModal from "../components/DistributionGroupModal";
import DetailsHeader from "../components/DetailsHeader";
import ExpiringAccessTab, { computeExpiringCandidates } from "../components/ExpiringAccessTab.jsx";
import BulkUserProjectAccess from "../components/BulkUserProjectAccess.jsx";
import ProjectPopup from "../components/ProjectPopup.jsx";
import PermissionAuditTab from "../components/PermissionAuditTab.jsx";
import UsersTab from "../components/UsersTab.jsx";
import { detailCardSx, detailItemSx, detailsGridSx } from "../styles/detailsLayout";
import { normalizeProjectUser, withWhitelistDisplay } from "../utils/api";
import { apiRequest } from "../utils/apiRequest";
import {
  ensureCompaniesAndProjectsLoaded,
  getCompanies,
  getProjectsForCompany,
  refreshForCurrentRoute,
  refreshProjectUsersForCompany,
  setCompanyUsersInStorage,
  updateProjectUsersInStorage,
} from "../utils/sessionStorageLayer";
import { showToast } from "../utils/toastOptions";
import { useBulkActions } from "../utils/useBulkActions";
import { useEventStream } from "../utils/useEventStream";
import { usePermissionTemplate } from "../utils/usePermissionTemplate";
import { useThresholdDays } from "../utils/useThresholdDays";
import { useUserSession } from "../utils/useUserSession";

const BASE = process.env.REACT_APP_API_BASE_URL;

const CHANGES_STATUS_STYLES = {
  new: { border: "#2e7d32", bg: "rgba(46,125,50,0.06)", label: "Ready" },
  existing: { border: "#1976d2", bg: "rgba(25,118,210,0.06)", label: "No Change" },
  invalid: { border: "#d32f2f", bg: "rgba(211,47,47,0.06)", label: "Invalid" },
};

const CHANGES_EDITABLE_COLUMNS = [
  { key: "email_address", label: "Email", width: "25%" },
  { key: "action_display", label: "Action", width: "25%" },
  { key: "project_name", label: "Project", width: "25%" },
  { key: "dist_group_name", label: "Distribution Group", width: "25%" },
];

const CHANGES_TEMPLATE_HEADERS = ["Email Address*", "Action*", "Project Name", "Distribution Group"];

const downloadChangesTemplate = () => {
  const csv = CHANGES_TEMPLATE_HEADERS.join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "daitaflow-bulk-changes-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const BULK_ACTION_DISPLAY = {
  add: "Add to Project",
  remove: "Remove from Project",
  deactivate: "Deactivate",
  reactivate: "Reactivate",
  whitelist: "Whitelist",
  unwhitelist: "Un-whitelist",
};

function buildChangesCommitMessage(data) {
  const parts = [];
  if (data?.added_to_project) parts.push(`${data.added_to_project} added to project`);
  if (data?.removed_from_project) parts.push(`${data.removed_from_project} removed from project`);
  if (data?.deactivated) parts.push(`${data.deactivated} deactivated`);
  if (data?.reactivated) parts.push(`${data.reactivated} reactivated`);
  if (data?.whitelisted) parts.push(`${data.whitelisted} whitelisted`);
  if (data?.unwhitelisted) parts.push(`${data.unwhitelisted} un-whitelisted`);
  return parts.join(", ") || "Complete";
}

export default function CompanyDetails() {
  const { companyId } = useParams();
  const { pathname } = useLocation();
  const companyIdNum = Number.parseInt(companyId, 10);

  // --- User/session from backend (no localStorage) ---
  const { user, loading: userLoading } = useUserSession();

  // Fetch permission template from backend (not stored client-side)
  const { template: permissionTemplate } = usePermissionTemplate();

  // --- Company view state ---
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [changeLogRefreshKey, setChangeLogRefreshKey] = useState(0);
  const [changeLogLiveUpdate, setChangeLogLiveUpdate] = useState(null);
  const [focusChangeLogId, setFocusChangeLogId] = useState(null);

  // Bulk actions & dialogs
  const [tabIndex, setTabIndex] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([0]));
  const handleTabChange = (_e, newValue) => {
    setTabIndex(newValue);
    setVisitedTabs((prev) => {
      if (prev.has(newValue)) return prev;
      const next = new Set(prev);
      next.add(newValue);
      return next;
    });
  };

  const {
    thresholdDays,
    setThresholdDays,
    inputDays,
    setInputDays,
    showConfirm,
    handleInputChange,
    handleSubmit,
    handleCancel,
    updateThresholdDays,
  } = useThresholdDays({ variant: "company" });
  const handleConfirm = async () => {
    await updateThresholdDays(company);
  };

  const bulk = useBulkActions({ getProjectsForCompany });
  const {
    selectedUsers,
    setSelectedUsers,
    openBulkAction,
    bulkActionType,
    setBulkActionType,
    displayProjects,
    selectedProjectIds,
    setSelectedProjectIds,
    previewOpen,
    handlePreviewOpen,
    handlePreviewClose,
    submitting,
    setSubmitting,
    showWhitelistModal,
    setShowWhitelistModal,
    showUserStatusModal,
    setShowUserStatusModal,
    showProjectAccessModal,
    setShowProjectAccessModal,
    handleCloseBulkAction,
    resetBulkState,
    openProjectPicker,
  } = bulk;

  const [loadingAction, setLoadingAction] = useState(null);
  const [showDistGroupModal, setShowDistGroupModal] = useState(false);
  const [confirmRestoreUserId, setConfirmRestoreUserId] = useState(null);

  // Load Distribution Group members into table selection
  const [distGroups, setDistGroups] = useState([]);
  const [loadingDistGroups, setLoadingDistGroups] = useState(false);
  const [externalSelection, setExternalSelection] = useState(null);

  const fetchDistGroups = useCallback(async () => {
    if (!companyIdNum) return;
    setLoadingDistGroups(true);
    try {
      const data = await apiRequest(
        `${BASE}/companies/${companyIdNum}/distribution-groups/`,
        { method: "GET" }
      );
      setDistGroups(Array.isArray(data) ? data : []);
    } catch {
      setDistGroups([]);
    } finally {
      setLoadingDistGroups(false);
    }
  }, [companyIdNum]);

  const handleLoadGroupMembers = useCallback(
    (group) => {
      if (!group || !group.users?.length) {
        setExternalSelection(null);
        return;
      }
      const groupEmails = new Set(
        group.users
          .map((u) => (u.login || u.email_address || "").toLowerCase().trim())
          .filter(Boolean)
      );
      const matchedIds = (companyUsers || [])
        .filter((u) => groupEmails.has((u.email_address || "").toLowerCase().trim()))
        .map((u) => u.id);
      if (matchedIds.length === 0) {
        showToast("warning", "None of the group members were found in the company directory.");
        return;
      }
      setExternalSelection(matchedIds);
      if (matchedIds.length < group.users.length) {
        showToast(
          "info",
          `Loaded ${matchedIds.length} of ${group.users.length} group members (others not in directory).`
        );
      } else {
        showToast("success", `Selected ${matchedIds.length} group member${matchedIds.length === 1 ? "" : "s"}.`);
      }
    },
    [companyUsers]
  );

  // Bulk preview dialog state (table-driven actions routed through preview→commit)
  const [bulkPreviewRows, setBulkPreviewRows] = useState(null);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);

  const isAdmin = useMemo(() => {
    return permissionTemplate?.[companyId]?.user_access_levels?.admin === 4;
  }, [permissionTemplate, companyId]);

  // Refresh: route-aware re-fetch via session storage layer, then reload page data
  const handleRefresh = async () => {
    if (loadingAction) return;
    setLoadingAction("refresh");
    try {
      await refreshForCurrentRoute(pathname);
      await fetchData(true);
    } catch (error) {
      showToast("error", error.message || "Failed to refresh");
    } finally {
      setLoadingAction(null);
    }
  };

  // --- Actions ---
  const handleRestoreUser = (userId) => {
    setConfirmRestoreUserId(userId);
  };

  const executeRestoreUser = async () => {
    const userId = confirmRestoreUserId;
    setConfirmRestoreUserId(null);
    if (loadingAction || !userId) return;
    if (!user?.daitaflow_user_id) {
      showToast("error", "Not signed in.");
      return;
    }
    setLoadingAction("restoreUser");
    try {
      await apiRequest(
        `${BASE}/companies/${companyIdNum}/users/reactivate/${userId}/`,
        {
          method: "PATCH",
          body: {
            target_user_id: userId,
          },
        }
      );
      showToast("success", "User reactivated successfully");
      fetchData(true);
    } catch (error) {
      showToast("error", error.message || "Failed to reactivate user");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleBulkAction = (type) => bulk.handleBulkAction(type, companyIdNum);

  // Re-fetch company users only (used for all actions)
  async function reloadCompanyUsers(forceRefresh = false) {
    try {
      const data = await apiRequest(
        `${BASE}/companies/${companyIdNum}/users/`,
        { method: "GET", forceRefresh }
      );
      const raw = data.company_users || [];
      setCompanyUsersInStorage(companyIdNum, raw);
      setCompanyUsers(raw.map(withWhitelistDisplay));
    } catch (error) {
      showToast("error", error.message || "Failed to refresh company users");
      throw error;
    }
  }

  // Re-fetch users for specific projects and merge into storage via layer
  async function refreshProjectUsers(projectIds = [], forceRefresh = false) {
    if (!projectIds.length) return;

    try {
      const reqs = projectIds.map(async (pid) => {
        const cid = companyIdNum;
        if (!cid) {
          return { pid, users: [], error: new Error(`Project ${pid} missing company_id`) };
        }
        try {
          const data = await apiRequest(
            `${BASE}/companies/${cid}/projects/${pid}/users/`,
            { method: "GET", forceRefresh, suppressErrorToast: true }
          );
          return {
            pid,
            users: (data.project_users || []).map(normalizeProjectUser),
          };
        } catch (err) {
          return { pid, users: [], error: err };
        }
      });

      const results = await Promise.all(reqs);
      results.forEach(({ pid, users, error }) => {
        if (error && error.status !== 404) {
          showToast("error", error.message || `Failed to fetch users for project ${pid}`);
        }
        updateProjectUsersInStorage(companyIdNum, pid, error ? [] : users);
      });
    } catch (error) {
      showToast("error", error.message || "Failed to refresh project users");
    }
  }

  const handleConfirmBulkAction = async () => {
    const action = bulkActionType;
    if (!action) {
      showToast("error", "No action selected.");
      return;
    }
    if (!user?.daitaflow_user_id) {
      showToast("error", "Not signed in.");
      return;
    }

    const selectedUserIds = Array.from(selectedUsers || []);
    if (!selectedUserIds.length) {
      showToast("error", "Select at least one user.");
      return;
    }

    // Close all action-selection modals
    handleCloseBulkAction();
    setShowWhitelistModal(false);
    setShowUserStatusModal(false);
    setShowProjectAccessModal(false);

    setLoadingAction(action);
    try {
      if (action === "add" || action === "remove") {
        if (selectedProjectIds.size === 0) {
          showToast("error", "Select at least one project.");
          return;
        }
        const pairs = [];
        Array.from(selectedProjectIds).forEach((pid) => {
          selectedUserIds.forEach((uid) => {
            pairs.push({ user_id: uid, project_id: pid });
          });
        });
        const endpoint = action === "add" ? "bulk_add" : "bulk_remove";
        await apiRequest(
          `${BASE}/companies/${companyIdNum}/users/${endpoint}/`,
          { method: "POST", body: { users: pairs } }
        );
        showToast("success", `${action === "add" ? "Added" : "Removed"} ${selectedUserIds.length} user(s) ${action === "add" ? "to" : "from"} ${selectedProjectIds.size} project(s).`);
      } else if (action === "deactivate") {
        await apiRequest(
          `${BASE}/companies/${companyIdNum}/users/bulk_deactivate/`,
          { method: "POST", body: { user_ids: selectedUserIds } }
        );
        showToast("success", `Deactivated ${selectedUserIds.length} user(s).`);
      } else if (action === "reactivate") {
        await apiRequest(
          `${BASE}/companies/${companyIdNum}/users/bulk_reactivate/`,
          { method: "POST", body: { user_ids: selectedUserIds } }
        );
        showToast("success", `Reactivated ${selectedUserIds.length} user(s).`);
      } else if (action === "whitelist") {
        await apiRequest(
          `${BASE}/companies/${companyIdNum}/users/whitelist/`,
          { method: "POST", body: { user_ids: selectedUserIds } }
        );
        showToast("success", `Whitelisted ${selectedUserIds.length} user(s).`);
      } else if (action === "unwhitelist") {
        await apiRequest(
          `${BASE}/companies/${companyIdNum}/users/unwhitelist/`,
          { method: "POST", body: { user_ids: selectedUserIds } }
        );
        showToast("success", `Un-whitelisted ${selectedUserIds.length} user(s).`);
      }

      await reloadCompanyUsers(true);
      if (isAdmin) setChangeLogRefreshKey((k) => k + 1);
      resetBulkState();
    } catch (e) {
      showToast("error", e.message || `Failed to ${action}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleBulkPreviewClose = () => {
    setBulkPreviewOpen(false);
    setBulkPreviewRows(null);
    resetBulkState();
  };

  const handleBulkPreviewCommitSuccess = async () => {
    setBulkPreviewOpen(false);
    setBulkPreviewRows(null);
    await reloadCompanyUsers(true);
    if (isAdmin) {
      setChangeLogRefreshKey((k) => k + 1);
    }
    resetBulkState();
  };

  // --- Load company (from storage; ensure companies loaded if missing) ---
  const findOrFetchCompany = async (forceRefresh) => {
    let companies = getCompanies() || [];
    let foundCompany = companies.find((c) => c.id === companyIdNum);

    if (!foundCompany) {
      try {
        await ensureCompaniesAndProjectsLoaded(forceRefresh);
        companies = getCompanies() || [];
        foundCompany = companies.find((c) => c.id === companyIdNum);
      } catch (error) {
        showToast("error", error.message || "Failed to fetch companies");
      }
    }

    return foundCompany;
  };

  const fetchCompanyUsers = async (forceRefresh) => {
    try {
      const companyUsersData = await apiRequest(
        `${BASE}/companies/${companyIdNum}/users/`,
        { method: "GET", forceRefresh }
      );
      const raw = companyUsersData.company_users || [];
      setCompanyUsersInStorage(companyIdNum, raw);
      setCompanyUsers(raw.map(withWhitelistDisplay));
    } catch (error) {
      showToast("error", error.message || "Failed to fetch company users");
    }
  };

  const handleInactiveDaysThreshold = async (inactiveDaysThreshold, foundCompany) => {
    if (inactiveDaysThreshold.status === "fulfilled") {
      const inactiveDaysThresholdData = inactiveDaysThreshold.value;
      setThresholdDays(inactiveDaysThresholdData.company_inactive_days_threshold);
      setInputDays(inactiveDaysThresholdData.company_inactive_days_threshold);
    } else {
      const err = inactiveDaysThreshold.reason;
      if (err?.status === 404) {
        await updateThresholdDays(foundCompany);
      } else {
        showToast("error", err?.message || "Failed to fetch inactive days threshold");
      }
    }
  };

  const fetchAdminData = async (foundCompany, forceRefresh) => {
    try {
      const inactiveDaysThreshold = await apiRequest(
        `${BASE}/companies/${foundCompany.id}/inactive-days-threshold/`,
        { method: "GET", forceRefresh, suppressErrorToast: true }
      );
      await handleInactiveDaysThreshold(
        { status: "fulfilled", value: inactiveDaysThreshold },
        foundCompany
      );
    } catch (error) {
      showToast("error", error.message || "Failed to fetch admin data");
    }
  };

  const fetchData = async (forceRefresh = false) => {
    if (!user) {
      setError("User session not available.");
      setLoading(false);
      return;
    }

    const foundCompany = await findOrFetchCompany(forceRefresh);
    setCompany(foundCompany);

    if (!foundCompany) {
      const errorMsg = "Company not found. Please go back to the dashboard.";
      setError(errorMsg);
      showToast("error", "Company not found.");
      setLoading(false);
      return;
    }

    await fetchCompanyUsers(forceRefresh);

    if (isAdmin) {
      await fetchAdminData(foundCompany, forceRefresh);
    }

    setLoading(false);
  };

  // Initial + whenever id/user/admin changes
  useEffect(() => {
    if (!userLoading) {
      setLoading(true);
      setError(null);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userLoading, isAdmin]);

  const changeLogAffectsProjectUsers = useCallback((log) => {
    const actions = ["Add to Project", "Remove from Project", "Deactivate User"];
    return (log?.entries || []).some((e) => actions.includes(e?.action));
  }, []);

  useEventStream({
    companyIds: company?.id ? [company.id] : [],
    enabled: !!user && !!company?.id && isAdmin,
    onConnected: () => setChangeLogRefreshKey((k) => k + 1),
    onChangeLogUpdated: (data) => {
      if (Number(data?.company_id) !== Number(company?.id)) return;
      if (data.log) {
        setChangeLogLiveUpdate({ log: data.log, ts: Date.now() });
        if (changeLogAffectsProjectUsers(data.log)) {
          refreshProjectUsersForCompany(company.id, true);
        }
      } else {
        setChangeLogRefreshKey((k) => k + 1);
        refreshProjectUsersForCompany(company.id, true);
      }
    },
  });

  const companyProjects = useMemo(
    () => getProjectsForCompany(companyIdNum) || [],
    // re-derive after every data fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyIdNum, isLoading]
  );

  const expiringCount = useMemo(() => {
    if (!isAdmin || !thresholdDays) return null;
    return computeExpiringCandidates({
      companyUsers,
      projects: companyProjects,
      companyThreshold: thresholdDays,
    }).length;
  }, [isAdmin, thresholdDays, companyUsers, companyProjects]);

  // --- Rendering ---
  // Show error toast when errors occur
  useEffect(() => {
    if (error) {
      showToast("error", error);
    }
  }, [error]);

  if (userLoading || isLoading) {
    return (
      <div className="spinner-overlay" role="status" aria-label="Loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="page">
        <div className="company-details-container">
          <Typography
            variant="h6"
            sx={{ padding: "2rem", textAlign: "center", color: "text.secondary" }}
          >
            Company not found
          </Typography>
        </div>
      </div>
    );
  }

  const renderDetail = (label, value) => {
    if (value === null || value === undefined || value === "") return null;
    let displayValue = value;
    if (typeof value === "boolean") {
      displayValue = value ? (
        <span style={{ color: "green" }}>Yes</span>
      ) : (
        <span style={{ color: "red" }}>No</span>
      );
    } else if (label.includes("Date") || label.includes("At")) {
      displayValue = new Date(value).toLocaleString();
    }
    return (
      <Box sx={detailItemSx}>
        <span className="detail-label">{label}:</span>
        <span className="detail-value">{displayValue}</span>
      </Box>
    );
  };

  const changesPreviewUrl = `${BASE}/companies/${companyIdNum}/csv/changes/preview/`;
  const changesCommitUrl = `${BASE}/companies/${companyIdNum}/csv/changes/commit/`;

  return (
    <div className="page">
      <DetailsHeader
        title={`Company: ${company.name}`}
        onBack={() => window.history.back()}
        onRefresh={handleRefresh}
        sx={{ margin: "0.5rem 0 2rem" }}
      >
        <Box sx={detailsGridSx}>
          {renderDetail("ID", company.id)}
          {renderDetail("Is Active", company.is_active)}
          {renderDetail("Total Users", companyUsers.length)}
          {expiringCount !== null && renderDetail("Expiring Access", expiringCount)}
          {renderDetail("Projects", companyProjects.length)}
        </Box>
      </DetailsHeader>

      <ContentCard
        elevation={1}
        p={3}
        className="project-details-container project-users-container"
        sx={{ ...detailCardSx, pt: 0 }}
      >
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="users-admins-tabs">
          <Tab label="Users" />
          {isAdmin && <Tab label="Admin Controls" />}
          {isAdmin && <Tab label="Bulk Changes" />}
          {isAdmin && <Tab label="Permission Audit" />}
          {isAdmin && <Tab label="Expiring Access" />}
          {isAdmin && <Tab label="Change Logs" />}
        </Tabs>

        <Box sx={{ minHeight: 0 }}>
          {visitedTabs.has(0) && (
            <Box hidden={tabIndex !== 0} sx={{ minHeight: 0 }}>
              <UsersTab users={companyUsers || []} paginationKey={`users:company:${companyIdNum}`} />
            </Box>
          )}

          {isAdmin && visitedTabs.has(1) && (
            <Box hidden={tabIndex !== 1} sx={{ minHeight: 0 }}>
              <AdminTab
                variant="company"
                users={companyUsers || []}
                thresholdDays={thresholdDays}
                inputDays={inputDays}
                onInputChange={handleInputChange}
                onSubmit={handleSubmit}
                showConfirmDialog={showConfirm}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                handleRestoreUser={handleRestoreUser}
                checkboxSelection={true}
                onSelectionChange={setSelectedUsers}
                externalSelection={externalSelection}
                paginationKey={`admin:company:${companyIdNum}`}
                companyName={company?.name || ""}
                distributionGroups={distGroups}
                distributionGroupsLoading={loadingDistGroups}
                onDistributionGroupsOpen={fetchDistGroups}
                onLoadDistributionGroup={handleLoadGroupMembers}
                onClearDistributionGroup={() => setExternalSelection(null)}
                onUsersImported={async () => {
                  await reloadCompanyUsers(true);
                  setChangeLogRefreshKey((k) => k + 1);
                }}
              />

              {/* Bulk Action Dock — hide when any bulk-action modal is open */}
              <Slide
                in={
                  selectedUsers.size > 0 &&
                  !openBulkAction &&
                  !showWhitelistModal &&
                  !showUserStatusModal &&
                  !showProjectAccessModal &&
                  !showDistGroupModal &&
                  !bulkPreviewOpen
                }
                direction="up"
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: "2vh",
                    display: "flex",
                    justifyContent: "center",
                    zIndex: (t) => t.zIndex.snackbar,
                    pointerEvents: "none",
                  }}
                >
                  <ContentCard
                    elevation={3}
                    p={2}
                    sx={{
                      pointerEvents: "auto",
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      flexWrap: "wrap",
                      maxWidth: "min(960px, calc(100vw - 32px))",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ fontWeight: 600 }}>Bulk Actions</span>
                      <span style={{ color: "gray" }}>
                        {selectedUsers.size} user{selectedUsers.size === 1 ? "" : "s"} selected
                      </span>
                    </div>
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={!!loadingAction}
                      onClick={() => handleBulkAction("project_access")}
                    >
                      Project Access
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!!loadingAction}
                      onClick={() => handleBulkAction("whitelist")}
                    >
                      Update Whitelist
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!!loadingAction}
                      sx={{ bgcolor: "#5c6bc0", "&:hover": { bgcolor: "#3949ab" } }}
                      onClick={() => handleBulkAction("user_status")}
                    >
                      Deactivate / Reactivate Users
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!!loadingAction}
                      sx={{ bgcolor: "#26a69a", "&:hover": { bgcolor: "#00897b" } }}
                      onClick={() => setShowDistGroupModal(true)}
                    >
                      Distribution Group
                    </Button>
                  </ContentCard>
                </Box>
              </Slide>

              {/* Project Selection Dialog */}
              <ProjectPopup
                open={openBulkAction}
                onClose={handleCloseBulkAction}
                bulkActionType={bulkActionType}
                displayProjects={displayProjects}
                selectedProjectIds={selectedProjectIds}
                onSelectedProjectIdsChange={setSelectedProjectIds}
                onPreview={handlePreviewOpen}
                onSubmit={handleConfirmBulkAction}
                submitting={submitting}
                previewOpen={previewOpen}
                onPreviewClose={handlePreviewClose}
                selectedUserIds={Array.from(selectedUsers || [])}
                companyUsers={companyUsers || []}
                allProjects={displayProjects}
              />

              {/* Whitelist or Un-whitelist users */}
              <BulkActionChoiceModal
                open={showWhitelistModal}
                onClose={() => {
                  setShowWhitelistModal(false);
                  setBulkActionType(null);
                }}
                title="Whitelist or Un-whitelist users"
                options={[
                  {
                    value: "whitelist",
                    label: "Whitelist",
                    color: "success",
                    description:
                      "Whitelisting protects users from automated removal by inactivity rules.",
                  },
                  {
                    value: "unwhitelist",
                    label: "Un-whitelist",
                    color: "warning",
                    description: "Un-whitelisting allows them to be removed by inactivity rules.",
                  },
                ]}
                value={
                  bulkActionType === "whitelist" || bulkActionType === "unwhitelist"
                    ? bulkActionType
                    : null
                }
                onValueChange={setBulkActionType}
                selectedUserIds={Array.from(selectedUsers || [])}
                companyUsers={companyUsers || []}
                getStatusLabel={(u, v) =>
                  v === "whitelist" && u.is_whitelisted
                    ? "(already whitelisted)"
                    : v === "unwhitelist" && !u.is_whitelisted
                      ? "(not whitelisted)"
                      : null
                }
                onConfirm={handleConfirmBulkAction}
                submitting={submitting}
              />

              {/* Project access: bulk Add/Remove buttons + per-user filtered grid */}
              <Dialog
                open={showProjectAccessModal}
                onClose={() => setShowProjectAccessModal(false)}
                fullWidth
                maxWidth="md"
              >
                <DialogTitle>Project Access</DialogTitle>
                <DialogContent dividers>
                  <Typography sx={{ mb: 2 }}>
                    Add users to projects or remove them from projects for the {selectedUsers.size}{" "}
                    selected user{selectedUsers.size === 1 ? "" : "s"}.
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
                    <Button
                      variant={bulkActionType === "add" ? "contained" : "outlined"}
                      color="warning"
                      onClick={() => openProjectPicker("add", companyIdNum)}
                    >
                      Add to Projects
                    </Button>
                    <Button
                      variant={bulkActionType === "remove" ? "contained" : "outlined"}
                      color="error"
                      onClick={() => openProjectPicker("remove", companyIdNum)}
                    >
                      Remove from Projects
                    </Button>
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Current project assignments for selected users
                  </Typography>
                  <BulkUserProjectAccess
                    companyId={companyIdNum}
                    selectedUserIds={Array.from(selectedUsers || [])}
                    companyUsers={companyUsers || []}
                    isAdmin={isAdmin}
                    onAfterRemove={async () => {
                      await reloadCompanyUsers(true);
                      setChangeLogRefreshKey((k) => k + 1);
                    }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowProjectAccessModal(false)}>Cancel</Button>
                </DialogActions>
              </Dialog>

              {/* User status: Deactivate or Reactivate */}
              <BulkActionChoiceModal
                open={showUserStatusModal}
                onClose={() => {
                  setShowUserStatusModal(false);
                  setBulkActionType(null);
                }}
                title="Deactivate or Reactivate users"
                options={[
                  {
                    value: "deactivate",
                    label: "Deactivate",
                    color: "error",
                    description:
                      "Deactivation removes project access first, then removes company access in Procore.",
                  },
                  {
                    value: "reactivate",
                    label: "Reactivate",
                    color: "success",
                    description: "Reactivation restores previously deactivated users.",
                  },
                ]}
                value={
                  bulkActionType === "deactivate" || bulkActionType === "reactivate"
                    ? bulkActionType
                    : null
                }
                onValueChange={setBulkActionType}
                selectedUserIds={Array.from(selectedUsers || [])}
                companyUsers={companyUsers || []}
                getStatusLabel={(u, v) =>
                  v === "deactivate" && !u.is_active
                    ? "(already inactive)"
                    : v === "reactivate" && u.is_active
                      ? "(already active)"
                      : null
                }
                currentUserProcoreId={user?.procore_user_id ?? null}
                onConfirm={handleConfirmBulkAction}
                submitting={submitting}
              />

              {/* Bulk Preview Dialog — table-driven actions routed through preview→commit */}
              <Dialog
                open={bulkPreviewOpen}
                onClose={handleBulkPreviewClose}
                fullWidth
                maxWidth="lg"
              >
                <DialogTitle>Review Bulk Changes</DialogTitle>
                <DialogContent dividers sx={{ minHeight: 300 }}>
                  {bulkPreviewRows && (
                    <CsvWorkflow
                      hideUpload
                      previewUrl={changesPreviewUrl}
                      commitUrl={changesCommitUrl}
                      editableColumns={CHANGES_EDITABLE_COLUMNS}
                      statusStyles={CHANGES_STATUS_STYLES}
                      initialRows={bulkPreviewRows}
                      onCommitSuccess={handleBulkPreviewCommitSuccess}
                      buildCommitMessage={buildChangesCommitMessage}
                    />
                  )}
                </DialogContent>
              </Dialog>

              {/* Distribution Group — create or add users to a group */}
              <DistributionGroupModal
                open={showDistGroupModal}
                onClose={() => setShowDistGroupModal(false)}
                companyId={companyIdNum}
                selectedUserIds={Array.from(selectedUsers || [])}
                companyUsers={companyUsers || []}
                projects={getProjectsForCompany(companyIdNum) || []}
              />
            </Box>
          )}

          {/* Bulk Changes — CSV upload */}
          {isAdmin && visitedTabs.has(2) && (
            <Box hidden={tabIndex !== 2} sx={{ py: 2 }}>
              <CsvWorkflow
                helperText="Columns: Email Address*, Action*, Project Name, Distribution Group. Actions: Add to Project, Remove from Project, Deactivate, Reactivate, Whitelist, Un-whitelist, Add to Distribution Group, Remove from Distribution Group."
                previewUrl={changesPreviewUrl}
                commitUrl={changesCommitUrl}
                editableColumns={CHANGES_EDITABLE_COLUMNS}
                onDownloadTemplate={downloadChangesTemplate}
                statusStyles={CHANGES_STATUS_STYLES}
                onCommitSuccess={async () => {
                  await reloadCompanyUsers(true);
                  setChangeLogRefreshKey((k) => k + 1);
                }}
                buildCommitMessage={buildChangesCommitMessage}
                onViewChangeLog={(logId) => {
                  setFocusChangeLogId(logId);
                  handleTabChange(null, 5);
                }}
              />
            </Box>
          )}

          {/* Permission Audit */}
          {isAdmin && visitedTabs.has(3) && (
            <Box hidden={tabIndex !== 3} sx={{ py: 2 }}>
              <PermissionAuditTab variant="company" companyId={companyIdNum} paginationKey={`audit:company:${companyIdNum}`} />
            </Box>
          )}

          {/* Expiring Access */}
          {isAdmin && visitedTabs.has(4) && (
            <Box hidden={tabIndex !== 4} sx={{ py: 2 }}>
              <ExpiringAccessTab
                companyId={companyIdNum}
                isAdmin={isAdmin}
                companyUsers={companyUsers || []}
                projects={getProjectsForCompany(companyIdNum) || []}
                companyThreshold={thresholdDays}
                onAfterRemove={async () => {
                  await reloadCompanyUsers(true);
                  setChangeLogRefreshKey((k) => k + 1);
                }}
              />
            </Box>
          )}

          {isAdmin && visitedTabs.has(5) && (
            <Box hidden={tabIndex !== 5} sx={{ minHeight: 0 }}>
              <ChangeLogsTab
                companyId={company?.id}
                refreshKey={changeLogRefreshKey}
                logUpdate={changeLogLiveUpdate}
                focusLogId={focusChangeLogId}
                onFocusHandled={() => setFocusChangeLogId(null)}
              />
            </Box>
          )}
        </Box>
      </ContentCard>
      {/* Restore user confirmation */}
      <Dialog
        open={!!confirmRestoreUserId}
        onClose={() => setConfirmRestoreUserId(null)}
        maxWidth="xs"
      >
        <DialogTitle>Reactivate User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to reactivate{" "}
            <strong>
              {(() => {
                const u = (companyUsers || []).find((u) => u.id === confirmRestoreUserId);
                return u?.name || u?.email_address || `User ${confirmRestoreUserId}`;
              })()}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreUserId(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={executeRestoreUser}>
            Reactivate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
